import { useContext } from 'react';
import { WebAPIContext } from '../../contexts/WebAPIContext';

// test

/** マニュアル情報の型定義 */
export type ManualType = {
  manualId: string;
  manualName: string;
  manualLink: string;
  updateDate?: string;
  isAdminOnly?: boolean;
};

/** API レスポンスの型定義 */
export type ManualAPIResponse = {
  status: 'success' | 'failed' | 'expired';
  message: string;
};

/**
 * マニュアルAPI用カスタムフック
 */
export const useManualAPI = () => {
  const WebAPI = useContext(WebAPIContext);

  /**
   * レスポンス処理
   */
  const handleResponse = (result: any) => {
    if (result.errors?.length > 0) {
      throw new Error(result.errors[0].message || 'GraphQL error');
    }
    const data = Object.values(result.data || {})[0];
    if ((data as any)?.status === 'failed') {
      throw new Error((data as any).message || 'API returned failure');
    }
    return data;
  };

  /**
   * マニュアル一覧を取得する
   */
  const getManual = async (): Promise<ManualType[]> => {
    const result = await WebAPI.request({
      query: `
        query {
          getManual {
            manualId
            manualName
            manualLink
            updateDate
            isAdminOnly
          }
        }`,
    });
    return handleResponse(result) as ManualType[];
  };

  /**
   * マニュアル名を指定して、削除されていない最新の1件を取得する
   * @param manualName - マニュアル名
   */
  const getManualByName = async (manualName: string): Promise<ManualType | null> => {
    const result = await WebAPI.request({
      query: `
        query getManualByName($input: getManualByNameInfo!) {
          getManualByName(input: $input) {
            manualId
            manualName
            manualLink
            updateDate
            isAdminOnly
          }
        }`,
      variables: {
        input: { manualName },
      },
    });
    return handleResponse(result) as ManualType | null;
  };

  /**
   * マニュアルを新規登録する
   * @param input - 登録データ
   */
  const putManual = async (input: { manualName: string; manualLink: string; isAdminOnly?: boolean }): Promise<ManualAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation putManual($input: putManualInfo) {
          putManual(input: $input) {
            message
            status
          }
        }
      `,
      variables: {
        input,
      },
    });
    return handleResponse(result) as ManualAPIResponse;
  };

  /**
   * マニュアルを更新する
   * @param input - 更新データ
   */
  const updateManual = async (input: { manualId: string; oldManualName?: string; manualName: string; manualLink: string; updateDate?: string; isAdminOnly?: boolean }): Promise<ManualAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation updateManual($input: updateManualInfo!) {
          updateManual(input: $input) {
            message
            status
          }
        }
      `,
      variables: {
        input,
      },
    });
    return handleResponse(result) as ManualAPIResponse;
  };

  /**
   * マニュアルを削除する
   * @param manualId - 削除対象のマニュアルID
   */
  const deletedManual = async (input: { manualId: string; manualName: string; updateDate?: string }): Promise<ManualAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation deletedManual($input: deletedManualInfo!) {
          deletedManual(input: $input) {
            message
            status
          }
        }`,
      variables: {
        input,
      },
    });
    return handleResponse(result) as ManualAPIResponse;
  };

  return {
    getManual,
    getManualByName,
    putManual,
    updateManual,
    deletedManual,
  };
};
